/** Pagina di dettaglio di una singola analisi AI: report completo, indicizzato per sezione (SPEC-007 §6). */

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { TransactionForm } from '@/features/portfolios/TransactionForm';
import { usePortfolios } from '@/features/portfolios/api';
import { ApiError } from '@/lib/api-client';
import { TX_TYPE_LABELS, formatCurrency, formatDate, formatDateTime, formatNumber, formatQuantity, labelFor } from '@/lib/format';
import { Alert, Badge, Card, Select, Spinner, StatCard, TableWrap } from '@/ui';
import { Markdown } from '@/ui/Markdown';
import {
  useAnalysisDetail,
  type AnalysisPortfolioContext,
  type AnalysisPrices,
  type AnalysisRating,
  type FullReport,
  type LinkedTransaction,
} from './ai-analysis-api';
import { PriceLadder } from './PriceLadder';
import { LEVEL_LABELS } from './analysis-price-labels';
import './price-ladder.css';
import { RATING_HINT, RATING_TONE } from './analysis-rating';
import './watchlist.css';

type Section = {
  id: string;
  title: string;
  blocks: Array<{ label: string; text: string | null }>;
};

function buildSections(report: FullReport): Section[] {
  return [
    {
      id: 'analisti',
      title: 'I. Analisti',
      blocks: [
        { label: 'Mercato', text: report.marketReport },
        { label: 'Sentiment', text: report.sentimentReport },
        { label: 'News', text: report.newsReport },
        { label: 'Fondamentali', text: report.fundamentalsReport },
      ],
    },
    {
      id: 'ricerca',
      title: 'II. Ricerca',
      blocks: [
        { label: 'Bull', text: report.bullHistory },
        { label: 'Bear', text: report.bearHistory },
        { label: 'Research Manager', text: report.researchManagerDecision },
      ],
    },
    {
      id: 'trading',
      title: 'III. Trading',
      blocks: [
        // Il libro che il trader aveva davanti, subito prima del suo piano: e'
        // la premessa del piano, non un'appendice.
        ...(report.portfolioContext ? [{ label: 'Portafoglio visto dagli agenti', text: report.portfolioContext }] : []),
        { label: 'Trader', text: report.traderPlan },
      ],
    },
    {
      id: 'rischio',
      title: 'IV. Rischio',
      blocks: [
        { label: 'Aggressivo', text: report.riskAggressive },
        { label: 'Conservativo', text: report.riskConservative },
        { label: 'Neutrale', text: report.riskNeutral },
      ],
    },
    {
      id: 'portfolio-manager',
      title: 'V. Portfolio Manager',
      blocks: [{ label: 'Decisione', text: report.riskManagerDecision }],
    },
  ];
}

/** Buy/Overweight suggeriscono un acquisto, Sell/Underweight una vendita; Hold resta alla scelta dell'utente. */
const SUGGESTED_TX_TYPE: Record<AnalysisRating, string> = {
  Buy: 'buy',
  Overweight: 'buy',
  Hold: 'buy',
  Underweight: 'sell',
  Sell: 'sell',
  // Come `Hold`: nessuna direzione suggerita, il modulo parte dal default e
  // sceglie l'utente. Qui a maggior ragione — non c'è un giudizio da seguire.
  Review: 'buy',
};

/**
 * Il libro che gli agenti avevano davanti (F-170), accanto al giudizio.
 *
 * La posizione sul titolo analizzato sta per prima ed e' la riga che cambia la
 * lettura: «Hold con 15 unita' in mano» e «Hold senza posizione» sono due
 * consigli diversi, e un lettore che vede solo il badge li confonde. Le altre
 * posizioni danno il peso relativo, la cassa dice quanto spazio c'era.
 */
function PortfolioContextCard({ context }: { context: AnalysisPortfolioContext }) {
  const held = context.position;
  return (
    <Card
      title="Portafoglio visto dall'analisi"
      subtitle={
        context.asOf
          ? `Cumulato dei portafogli con auto-trading live, allo snapshot del ${formatDate(context.asOf)}`
          : 'Cumulato dei portafogli con auto-trading live'
      }
    >
      <p>
        {held ? (
          <>
            <strong>Posizione sul titolo:</strong> {formatQuantity(held.quantity)} unità
            {held.averagePrice ? ` · carico medio ${formatCurrency(held.averagePrice, held.currency)}` : ' · carico non noto'}
          </>
        ) : (
          <>
            <strong>Nessuna posizione sul titolo</strong> al momento dell'analisi
          </>
        )}
      </p>
      <p className="text-muted">
        {held
          ? "Il giudizio è dimensionato su questa posizione: un «Hold» qui significa «non aggiungere», un «Buy» significa aggiungere a ciò che c'è già."
          : 'Il giudizio è dimensionato su un libro che non teneva questo titolo: un «Buy» qui significa aprire una posizione nuova.'}
      </p>
      <div className="portfolios__stats">
        <StatCard label="Liquidità disponibile" value={context.cash ? formatCurrency(context.cash, context.currency) : 'non nota'} />
        <StatCard label="Portafogli" value={context.portfolios.map((portfolio) => portfolio.name).join(', ') || '—'} />
        <StatCard
          label="Altre posizioni"
          value={context.others.length > 0 ? context.others.map((other) => `${other.ticker} ${formatQuantity(other.quantity)}`).join(' · ') : 'nessuna'}
        />
      </div>
    </Card>
  );
}

function LinkedTransactionCard({ transaction }: { transaction: LinkedTransaction }) {
  return (
    <div className="transaction-form__linked-transaction">
      <strong>{labelFor(TX_TYPE_LABELS, transaction.txType)}</strong>
      <span>{formatDateTime(transaction.txDate)}</span>
      <span>
        {transaction.quantity ? `${formatQuantity(transaction.quantity)} quote · ` : ''}
        {transaction.price ? `a ${formatCurrency(transaction.price, transaction.currency)} · ` : ''}
        {formatCurrency(transaction.amount, transaction.currency)}
      </span>
      {transaction.notes && <span className="text-muted">{transaction.notes}</span>}
    </div>
  );
}

function ActionTakenSection({
  instrumentId,
  instrumentName,
  analysisId,
  suggestedTxType,
}: {
  instrumentId: string;
  instrumentName: string;
  analysisId: string;
  suggestedTxType: string;
}) {
  const [open, setOpen] = useState(false);
  const portfoliosQuery = usePortfolios();
  const [portfolioId, setPortfolioId] = useState('');

  if (!open) {
    return (
      <button type="button" className="btn btn--secondary" onClick={() => setOpen(true)}>
        Registra l&apos;azione intrapresa
      </button>
    );
  }

  const portfolios = portfoliosQuery.data?.items ?? [];
  const selectedPortfolioId = portfolioId || portfolios[0]?.id || '';

  if (portfoliosQuery.isPending) return <Spinner />;
  if (portfolios.length === 0) {
    return (
      <Alert variant="info">
        Nessun portafoglio disponibile: importa prima i tuoi dati per poter registrare un movimento.
      </Alert>
    );
  }

  return (
    <div className="transaction-form">
      {portfolios.length > 1 && (
        <Select
          label="Portafoglio"
          value={selectedPortfolioId}
          onChange={(event) => setPortfolioId(event.target.value)}
          options={portfolios.map((portfolio) => ({ value: portfolio.id, label: portfolio.name }))}
        />
      )}
      <TransactionForm
        portfolioId={selectedPortfolioId}
        fixedInstrument={{ id: instrumentId, name: instrumentName }}
        sourceAnalysisId={analysisId}
        defaultTxType={suggestedTxType}
        onSuccess={() => setOpen(false)}
        onCancel={() => setOpen(false)}
      />
    </div>
  );
}

/**
 * I prezzi dell'analisi, indicizzati (F-068).
 *
 * Ogni livello è mostrato **nella valuta del report e in quella dell'utente**.
 * Le due non coincidono quasi mai — TradingAgents ragiona nella valuta di
 * quotazione del titolo, che per Londra sono per giunta i pence — e vedere solo
 * la prima costringe a una conversione a mente proprio sul numero che serve per
 * decidere.
 *
 * Con fiducia **bassa** non compare alcun importo convertito, e il motivo è
 * scritto: un valore convertito su una valuta indovinata è indistinguibile da
 * uno corretto, ed è il modo più efficace di ingannare chi legge.
 */
function PricesCard({ prices }: { prices: AnalysisPrices }) {
  const uncertain = prices.confidence === 'low';
  const hasLevels = prices.levels.length > 0;

  if (!hasLevels && !prices.timeHorizon) return null;

  return (
    <Card
      title="Livelli di prezzo"
      subtitle={
        prices.currency
          ? `Valori del report in ${prices.currency}${prices.unit === 'minor' ? ' (centesimi)' : ''}${
              uncertain ? '' : `, convertiti in ${prices.baseCurrency}`
            }`
          : 'Valuta del report non determinabile'
      }
      action={prices.timeHorizon ? <Badge tone="neutral">{prices.timeHorizon}</Badge> : undefined}
    >
      {uncertain && (
        <Alert variant="warning" title="Valuta non confermata">
          {prices.reason}. Gli importi sono riportati così come compaiono nel report, senza conversione.
        </Alert>
      )}

      {hasLevels && (
        <>
          <TableWrap>
            <table className="table table--compact">
              <thead>
                <tr>
                  <th>Livello</th>
                  <th className="numeric">Nel report</th>
                  <th className="numeric">In {prices.baseCurrency}</th>
                  <th className="col--secondary">Origine</th>
                </tr>
              </thead>
              <tbody>
                {prices.levels.map((level) => (
                  <tr key={level.kind}>
                    <td>{LEVEL_LABELS[level.kind]}</td>
                    <td className="numeric">
                      {formatCurrency(level.valueMajor, prices.currency ?? undefined)}
                      {prices.unit === 'minor' && (
                        <span className="analysis-detail__price-raw">{level.value} centesimi nel testo</span>
                      )}
                    </td>
                    <td className="numeric">
                      {level.valueBase ? formatCurrency(level.valueBase, prices.baseCurrency) : '—'}
                    </td>
                    <td className="col--secondary">
                      {/* Un livello reso dalla libreria è un dato, uno letto dal
                          modello è un'interpretazione: la differenza è visibile
                          invece di essere appiattita. */}
                      {level.source === 'llm' ? 'letto dal modello' : 'campo del report'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>

          <PriceLadder
            levels={prices.levels}
            referencePrice={prices.referencePrice}
            currency={prices.currency}
            unit={prices.unit}
          />
        </>
      )}

      {prices.referencePrice && (
        <p className="analysis-detail__price-reference">
          Quotazione di riferimento {formatCurrency(prices.referencePrice, prices.referenceCurrency ?? undefined)}
          {prices.referenceAt && <> · {formatDateTime(prices.referenceAt)}</>} — è il valore con cui la scala del report
          è stata verificata.
        </p>
      )}
    </Card>
  );
}

export function AnalysisDetailPage() {
  const { instrumentId, analysisId } = useParams<{ instrumentId: string; analysisId: string }>();
  const detailQuery = useAnalysisDetail(instrumentId ?? '', analysisId ?? '', Boolean(instrumentId && analysisId));

  if (detailQuery.isPending) {
    return (
      <div className="watchlist">
        <Spinner label="Caricamento del report…" />
      </div>
    );
  }
  if (detailQuery.isError) {
    return (
      <div className="watchlist">
        <Alert variant="error" title="Impossibile caricare il report">
          {detailQuery.error instanceof ApiError ? detailQuery.error.message : 'Errore imprevisto.'}
        </Alert>
      </div>
    );
  }

  const analysis = detailQuery.data;
  const targetLevel = analysis.prices.levels.find((level) => level.kind === 'target') ?? null;
  const sections = analysis.fullReport ? buildSections(analysis.fullReport) : [];
  const nonEmptySections = sections
    .map((section) => ({ ...section, blocks: section.blocks.filter((block) => block.text) }))
    .filter((section) => section.blocks.length > 0);
  const hasOutcome =
    analysis.status === 'completed' &&
    (analysis.executiveSummary || analysis.investmentThesis || analysis.priceTarget !== null || analysis.timeHorizon);

  return (
    <div className="watchlist">
      <header className="watchlist__header">
        <div>
          <Link to="/watchlists" className="analysis-detail__back">
            ← Torna alla watchlist
          </Link>
          <h1 className="watchlist__title">
            {analysis.instrument.name}
            {/* La scheda del titolo a un clic (TODO.md, 15/09/2026): il report
                dice cosa fare, la scheda dice cos'è e quanto quota. Nel titolo e
                non fra i campi, perché è una navigazione, non un dato del report. */}
            <Link
              to={`/instruments/${analysis.instrument.id}`}
              className="analysis-detail__title-link"
              title={`Apri la scheda di ${analysis.instrument.name}`}
              aria-label={`Apri la scheda di ${analysis.instrument.name}`}
            >
              ↗
            </Link>
          </h1>
          <p className="watchlist__subtitle">
            {analysis.instrument.symbol ?? analysis.instrument.isin ?? '—'} · richiesta il{' '}
            {formatDateTime(analysis.requestedAt)}
          </p>
        </div>
        <div className="analysis-detail__header-badges">
          {analysis.triggeredBy === 'market_open_trigger' && <Badge tone="accent">🏛️ Trigger di mercato</Badge>}
          {analysis.rating && (
            <Badge tone={RATING_TONE[analysis.rating]} title={RATING_HINT[analysis.rating] ?? undefined}>
              {analysis.rating}
            </Badge>
          )}
        </div>
      </header>

      {analysis.status !== 'completed' && (
        <Alert variant="info">
          {analysis.status === 'failed'
            ? (analysis.errorMessage ?? 'Analisi non riuscita.')
            : 'Analisi ancora in corso: il report completo comparirà qui a fine elaborazione.'}
        </Alert>
      )}

      {/* Un badge «Review» senza spiegazione si legge come un guasto. Qui c'è
          lo spazio per dire che cosa è successo davvero: l'analisi è riuscita,
          il verdetto no. */}
      {analysis.rating === 'Review' && <Alert variant="info">{RATING_HINT.Review}</Alert>}

      {hasOutcome && (
        <Card title="Sintesi">
          {analysis.executiveSummary && <p>{analysis.executiveSummary}</p>}
          {analysis.investmentThesis && <p>{analysis.investmentThesis}</p>}
          <div className="portfolios__stats">
            {/* L'obiettivo compare con la sua valuta, non più come numero nudo:
                «12862» su un titolo londinese non è un prezzo, sono pence, e
                mostrarlo senza unità è la cosa che questa feature corregge.
                Il valore arriva da `prices`, che conosce valuta e scala. */}
            <StatCard
              label="Obiettivo di prezzo"
              value={
                targetLevel
                  ? formatCurrency(targetLevel.valueMajor, analysis.prices.currency ?? undefined)
                  : formatNumber(analysis.priceTarget)
              }
              hint={targetLevel?.valueBase ? `≈ ${formatCurrency(targetLevel.valueBase, analysis.prices.baseCurrency)}` : undefined}
            />
            <StatCard label="Orizzonte temporale" value={analysis.prices.timeHorizon ?? analysis.timeHorizon ?? '—'} />
          </div>
        </Card>
      )}

      {analysis.portfolioContext && <PortfolioContextCard context={analysis.portfolioContext} />}

      {analysis.status === 'completed' && <PricesCard prices={analysis.prices} />}

      {analysis.llmSummary && (
        <Card title="Riepilogo (generato dal nostro LLM)" subtitle="Lo stesso testo inviato nella notifica Telegram">
          <p>{analysis.llmSummary}</p>
        </Card>
      )}

      {analysis.llmPortfolioComparison && (
        <Card title="Confronto con il tuo portafoglio">
          <p>{analysis.llmPortfolioComparison}</p>
        </Card>
      )}

      {analysis.status === 'completed' && (
        <Card title="Azioni intraprese" subtitle="Movimenti registrati a partire da questa analisi">
          {analysis.linkedTransactions.length > 0 && (
            <div className="portfolios__stats">
              {analysis.linkedTransactions.map((transaction) => (
                <LinkedTransactionCard key={transaction.id} transaction={transaction} />
              ))}
            </div>
          )}
          <ActionTakenSection
            instrumentId={analysis.instrument.id}
            instrumentName={analysis.instrument.name}
            analysisId={analysis.id}
            suggestedTxType={analysis.rating ? SUGGESTED_TX_TYPE[analysis.rating] : 'buy'}
          />
        </Card>
      )}

      {nonEmptySections.length > 0 && (
        <Card title="Indice">
          <nav className="analysis-detail__toc">
            {nonEmptySections.map((section) => (
              <a key={section.id} href={`#${section.id}`}>
                {section.title}
              </a>
            ))}
          </nav>
        </Card>
      )}

      {nonEmptySections.map((section) => (
        <Card key={section.id} title={<span id={section.id}>{section.title}</span>}>
          <div className="analysis-detail__section">
            {section.blocks.map((block) => (
              <div key={block.label} className="ai-analysis__report-block">
                <span className="ai-analysis__report-block-label">{block.label}</span>
                <Markdown content={block.text ?? ''} />
              </div>
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
}
